/**
 * ShallowWaterSolver — nearshore/contained water on the VIRTUAL PIPES model
 * (Mei, Decaudin & Hu 2007; the games-standard stable SWE discretization):
 * each cell holds a water column over a fixed bed; virtual pipes to the four
 * neighbours accelerate flow by the hydraulic head difference, outflows are
 * scaled so a cell can never drain below zero, and depths integrate the flux
 * divergence. Handles run-up, sloshing, dam breaks, sources and drains —
 * everything the wave-equation ripple field can't (it has no volume).
 *
 * Pure TypeScript with fixed-step integration (same time-debt pattern as
 * RippleField) — unit-testable in Node: volume conservation, dam-break
 * fronts, dry-cell safety.
 *
 * PATCH MODE (shader gaps R6, 2026-09-30 — `config.patch: true`; everything
 * below is ADDITIVE, the default solver runs its old code bit for bit). The
 * oracle of the GPU puddle patch (`ShallowWaterGpu`): the SAME discretisation
 * with what a rain patch needs —
 *   • per-cell SOURCES / SINKS applied after each flow step, in this order:
 *     rain in (m/s per cell), infiltration (m/s capacity per cell),
 *     evaporation (m/s), drains (a drain cell's water leaves the patch — still
 *     water: the sea, a lake, a river);
 *   • SOLID cells (a structure): every pipe touching one is closed (a wall);
 *   • Dagenais et al. 2018 ("Extended virtual pipes", C&G 76) VISCOSITY — the
 *     implicit per-pipe factor H²/(H² + 3·Δt·ν), H the source column's depth:
 *     thin films are slowed, deep pools barely, and it can never add energy;
 *   • OPEN EDGES AGAINST THE TERRAIN (`edgeBed`): the virtual neighbour beyond
 *     an edge is dry ground at its own bed, so water leaves only DOWNHILL
 *     (the plain open boundary drained a flat plaza through every edge);
 *   • a WHOLE-CELL SHIFT (`shift`) for a camera-following window
 *     (RippleField.setOrigin's pattern): depth and flux move by the integer
 *     offset, the new strips start dry with their bed from the caller;
 *   • SHEET DRAINAGE (the unresolved drains — hydrology's depression storage
 *     + runoff, as SCS / LISFLOOD rain-on-grid models it): water above a
 *     cell's RETENTION (its micro-hollow + a film) leaves at up to
 *     `sheetDrain` m/s, faded out where the water surface slopes (a flowing
 *     sheet or a stream keeps its water; a STAGNANT sheet on a flat the grid
 *     cannot drain does not). Without it every settlement plateau — a flat
 *     disc — flooded in a steady rain;
 *   • a LEDGER per term (m³) so a harness proves ΔV = rain − infiltration −
 *     evaporation − runoff − drained − boundary − shiftedOut + clampGain every step;
 *   • a caller-chosen fixed step (`stepSize`, the CFL step — `cflStep`) with a
 *     hitch clamp that can never freeze it (≥ two steps — the RippleField law).
 */

export interface ShallowWaterConfig {
  /** Domain size in world units. */
  sizeX: number;
  sizeZ: number;
  /** Cells per side. Default 96 (clamped 16..256). */
  resolution?: number;
  /** Bed height at local (x, z) — terrain under the water. Default flat 0. */
  bedHeight?: (x: number, z: number) => number;
  /** Initial water depth: constant or per-position. Default 0.5. */
  initialDepth?: number | ((x: number, z: number) => number);
  /** Flow damping 1/s (pipe friction). Default 0.08. */
  damping?: number;
  /** Open boundaries let water leave the domain (waves at a beach). Default false (tank). */
  openBoundaries?: boolean;
  /** Gravity. Default 9.81. */
  gravity?: number;
  // ── PATCH MODE (shader gaps R6, 2026-09-30) — see the header; absent = the old solver ──
  /** Run the patch substep (sources / sinks / solids / viscosity / edge beds / ledger). Default false. */
  patch?: boolean;
  /** Fixed integration step (s). Default 1/120 (unchanged). The patch passes its CFL step (`cflStep`). */
  stepSize?: number;
  /** The hitch clamp on the carried time debt (s). Default 0.25; never below two steps (it could freeze). */
  maxDebt?: number;
  /** Steps a single `step(dt)` may run at most (patch mode; the rest of the debt is dropped). Default unlimited. */
  maxSubsteps?: number;
  /** Kinematic viscosity (m²/s), Dagenais et al. 2018 — per pipe × H²/(H² + 3·Δt·ν). Default 0 (off). */
  viscosity?: number;
  /**
   * Beds one cell OUTSIDE the edges (patch + openBoundaries): the virtual neighbour is dry ground at that bed, so
   * water leaves only downhill. Length 4n: +x edge by row, −x edge by row, +z edge by column, −z edge by column.
   */
  edgeBed?: Float32Array;
  /** The water-surface slope over which sheet drainage fades out (from full at [0] to none at [1]). Default [0.005, 0.02]. */
  sheetSlope?: [number, number];
}

/** A patch's water budget since the last `resetLedger()`, m³ — ΔV = rain − infiltration − evaporation − runoff − drained − boundary − shiftedOut + clampGain. */
export interface ShallowWaterLedger {
  rain: number; infiltration: number; evaporation: number; runoff: number; drained: number; boundary: number; shiftedOut: number; clampGain: number;
}

/** smoothstep (the sheet-drainage fade). */
function sstep(a: number, b: number, x: number): number { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

export class ShallowWaterSolver {
  /**
   * The CFL step of the lumped-pipe model (A = l², the games-standard choice — Šťava 2008, Dagenais 2018): its
   * gravity wave travels at √(g·l) whatever the depth, and the symplectic update on the 5-point stencil is stable for
   * c·Δt·√2 ≤ l (Müller 2007's Δt < h/c per axis). `courant` ≤ 1 is the safety factor.
   */
  static cflStep(cellSize: number, gravity = 9.81, courant = 0.5): number {
    const c = Math.sqrt(gravity * cellSize);
    return (courant * cellSize) / (Math.SQRT2 * c);
  }
  readonly resolution: number;
  private readonly cfg: Required<Omit<ShallowWaterConfig, 'bedHeight' | 'initialDepth' | 'patch' | 'stepSize' | 'maxDebt' | 'maxSubsteps' | 'viscosity' | 'edgeBed' | 'sheetSlope'>>;
  private readonly bed: Float32Array;
  private depth: Float32Array;
  /** Outflow per cell per direction [+x, −x, +z, −z], m³/s. */
  private readonly flux: Float32Array;
  private readonly cellX: number;
  private readonly cellZ: number;
  private readonly cellArea: number;
  private timeDebt = 0;
  /** Fixed integration step (virtual pipes stays stable well above CFL). Default 1/120; the patch passes its CFL step. */
  private readonly stepSize: number;
  /** The hitch clamp (s) — never under two steps, or a step longer than it could never run. */
  private readonly maxDebt: number;
  // ── patch mode (shader gaps R6) ──
  private readonly patchMode: boolean;
  private readonly viscosity: number;
  private readonly maxSubsteps: number;
  private edgeBed: Float32Array | null;
  private solid: Uint8Array | null = null;
  private drain: Uint8Array | null = null;
  private rainRate: Float32Array | null = null;
  private infilRate: Float32Array | null = null;
  private evapRate = 0;
  private retain: Float32Array | null = null;
  private sheetDrain = 0;
  private readonly sheetSlope: [number, number];
  /** Per-cell water-surface slope over wet neighbours, taken in K1 from the pre-step state (K2's sheet fade). */
  private slopeScratch: Float32Array | null = null;
  /** Substeps run since construction (patch diagnostics). */
  substeps = 0;
  readonly ledger: ShallowWaterLedger = { rain: 0, infiltration: 0, evaporation: 0, runoff: 0, drained: 0, boundary: 0, shiftedOut: 0, clampGain: 0 };

  constructor(config: ShallowWaterConfig) {
    const resolution = Math.min(256, Math.max(16, Math.floor(config.resolution ?? 96)));
    this.resolution = resolution;
    this.cfg = {
      sizeX: config.sizeX,
      sizeZ: config.sizeZ,
      resolution,
      damping: Math.max(0, config.damping ?? 0.08),
      openBoundaries: config.openBoundaries ?? false,
      gravity: config.gravity ?? 9.81,
    };
    // The grid maps col/(n−1) across sizeX, so the spacing is sizeX/(n−1):
    // sizeX/n under-reported cellArea (volume, addWater, pipe K) by ~2 %.
    this.cellX = config.sizeX / Math.max(1, resolution - 1);
    this.cellZ = config.sizeZ / Math.max(1, resolution - 1);
    this.cellArea = this.cellX * this.cellZ;
    this.stepSize = config.stepSize && config.stepSize > 0 ? config.stepSize : 1 / 120;
    this.maxDebt = Math.max(config.maxDebt ?? 0.25, 2 * this.stepSize);
    this.patchMode = config.patch === true;
    this.viscosity = Math.max(0, config.viscosity ?? 0);
    this.maxSubsteps = config.maxSubsteps && config.maxSubsteps > 0 ? Math.floor(config.maxSubsteps) : Number.POSITIVE_INFINITY;
    this.edgeBed = config.edgeBed ?? null;
    this.sheetSlope = config.sheetSlope ?? [0.005, 0.02];

    const n = resolution;
    this.bed = new Float32Array(n * n);
    this.depth = new Float32Array(n * n);
    this.flux = new Float32Array(n * n * 4);
    const bedFn = config.bedHeight ?? (() => 0);
    const depthInit = config.initialDepth ?? 0.5;
    for (let row = 0; row < n; row++) {
      const z = (row / (n - 1) - 0.5) * config.sizeZ;
      for (let col = 0; col < n; col++) {
        const x = (col / (n - 1) - 0.5) * config.sizeX;
        const i = row * n + col;
        this.bed[i] = bedFn(x, z);
        const d = typeof depthInit === 'function' ? depthInit(x, z) : depthInit;
        this.depth[i] = Math.max(0, d);
      }
    }
  }

  /** Total water volume in the domain (conservation checks, tests). */
  totalVolume(): number {
    let sum = 0;
    for (let i = 0; i < this.depth.length; i++) sum += this.depth[i];
    return sum * this.cellArea;
  }

  /** Water SURFACE height (bed + depth) at local coords; bed height if dry. */
  surfaceAtLocal(x: number, z: number): number {
    const i = this.cellIndex(x, z);
    return this.bed[i] + this.depth[i];
  }

  depthAtLocal(x: number, z: number): number {
    return this.depth[this.cellIndex(x, z)];
  }

  /** Add (or with negative `amount`, drain) water in a gaussian blob [m³]. */
  addWater(localX: number, localZ: number, radius: number, amount: number): void {
    const n = this.resolution;
    const cx = (localX / this.cfg.sizeX + 0.5) * (n - 1);
    const cz = (localZ / this.cfg.sizeZ + 0.5) * (n - 1);
    const rc = Math.max(1, radius / Math.min(this.cellX, this.cellZ));
    // Normalize so the blob integrates to `amount` regardless of radius.
    let weightSum = 0;
    const cells: Array<[number, number]> = [];
    for (let row = Math.max(0, Math.floor(cz - rc * 2)); row <= Math.min(n - 1, Math.ceil(cz + rc * 2)); row++) {
      for (let col = Math.max(0, Math.floor(cx - rc * 2)); col <= Math.min(n - 1, Math.ceil(cx + rc * 2)); col++) {
        const w = Math.exp(-((col - cx) ** 2 + (row - cz) ** 2) / (rc * rc));
        if (w < 0.02) continue;
        weightSum += w;
        cells.push([row * n + col, w]);
      }
    }
    if (weightSum <= 0) return;
    const perWeight = amount / (weightSum * this.cellArea);
    for (const [i, w] of cells) {
      this.depth[i] = Math.max(0, this.depth[i] + perWeight * w);
    }
  }

  /** Advance the simulation (fixed substeps; hitch-clamped). */
  step(dt: number): void {
    this.timeDebt = Math.min(this.timeDebt + dt, this.maxDebt);
    if (this.patchMode) {
      // patch: at most maxSubsteps a call; a debt still over a step after them is DROPPED to the fraction, never
      // carried (a slow frame loses sim time, it never snowballs — and it can never stop the patch stepping)
      let k = 0;
      while (this.timeDebt >= this.stepSize && k < this.maxSubsteps) { this.patchSubstep(this.stepSize); this.timeDebt -= this.stepSize; k++; }
      if (this.timeDebt >= this.stepSize) this.timeDebt %= this.stepSize;
      return;
    }
    while (this.timeDebt >= this.stepSize) {
      this.substep(this.stepSize);
      this.timeDebt -= this.stepSize;
    }
  }

  // ── PATCH MODE API (shader gaps R6, 2026-09-30) ──

  /** The fixed step this solver integrates with (s). */
  get dtStep(): number { return this.stepSize; }
  /** The pipe constant g·A/l (lumped A = cell area). */
  get pipeK(): number { return (this.cfg.gravity * this.cellArea) / Math.max(this.cellX, this.cellZ); }
  /** Cell spacing (m) and area (m²). */
  get cell(): { x: number; z: number; area: number } { return { x: this.cellX, z: this.cellZ, area: this.cellArea }; }
  /** The outflow fluxes per cell [+x, −x, +z, −z] (m³/s) — row-major, 4 per cell. */
  get fluxData(): Float32Array { return this.flux; }
  /** Run exactly one patch substep of `h` seconds (parity tests, fast-forward). */
  substepOnce(h: number): void { if (this.patchMode) this.patchSubstep(h); else this.substep(h); }
  /** Closed cells (1 = a wall: every pipe touching it carries nothing). null = none. */
  setSolid(mask: Uint8Array | null): void { this.solid = mask; }
  /** Drain cells (1 = still water: whatever reaches it leaves the patch, into `ledger.drained`). null = none. */
  setDrain(mask: Uint8Array | null): void { this.drain = mask; }
  /** Per-cell rain (m/s, already × exposure), per-cell infiltration capacity (m/s), and the evaporation rate (m/s). */
  setSources(s: { rain?: Float32Array | null; infiltration?: Float32Array | null; evaporation?: number }): void {
    if (s.rain !== undefined) this.rainRate = s.rain;
    if (s.infiltration !== undefined) this.infilRate = s.infiltration;
    if (s.evaporation !== undefined) this.evapRate = Math.max(0, s.evaporation);
  }
  /** Per-cell RETENTION (m: the micro-hollow + a film) above which a stagnant sheet drains, and the drain rate (m/s). */
  setSheetDrain(retain: Float32Array | null, rate: number): void { this.retain = retain; this.sheetDrain = Math.max(0, rate); }
  /** Replace the edge beds (length 4n, see the config). */
  setEdgeBed(b: Float32Array | null): void { this.edgeBed = b; }
  /** Zero the ledger (a harness brackets a window with it). */
  resetLedger(): void { const L = this.ledger; L.rain = L.infiltration = L.evaporation = L.runoff = L.drained = L.boundary = L.shiftedOut = L.clampGain = 0; }
  /** Overwrite a cell's bed (a bed rebuild under standing water keeps the water column). */
  setBedAt(i: number, h: number): void { this.bed[i] = h; }
  /** Overwrite a cell's depth (tests / seeding; not ledgered). */
  setDepthAt(i: number, d: number): void { this.depth[i] = Math.max(0, d); }

  /**
   * Move the window by (dc, dr) WHOLE cells (RippleField.setOrigin's pattern): new[row][col] = old[row + dr][col + dc]
   * for depth and flux (the water stays where it fell while the window slides under it); cells that leave the window
   * are counted in `ledger.shiftedOut`; the cells that slide in start dry with the bed `fill(col, row)` gives. The
   * solid / drain / source arrays are the CALLER's (it rebuilds them for the new window). Returns the cells moved in.
   */
  shift(dc: number, dr: number, fill: (col: number, row: number) => number): number {
    if (dc === 0 && dr === 0) return 0;
    const n = this.resolution;
    let lost = 0;
    for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
      const sc = col, sr = row; // a cell that no new cell maps from is shifted out
      const nc = sc - dc, nr = sr - dr;
      if (nc < 0 || nc >= n || nr < 0 || nr >= n) lost += this.depth[sr * n + sc];
    }
    this.ledger.shiftedOut += lost * this.cellArea;
    const d2 = new Float32Array(n * n), f2 = new Float32Array(n * n * 4), b2 = new Float32Array(n * n);
    let filled = 0;
    for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
      const i = row * n + col, sc = col + dc, sr = row + dr;
      if (sc >= 0 && sc < n && sr >= 0 && sr < n) {
        const j = sr * n + sc;
        d2[i] = this.depth[j]; b2[i] = this.bed[j];
        f2[i * 4] = this.flux[j * 4]; f2[i * 4 + 1] = this.flux[j * 4 + 1]; f2[i * 4 + 2] = this.flux[j * 4 + 2]; f2[i * 4 + 3] = this.flux[j * 4 + 3];
      } else { b2[i] = fill(col, row); filled++; }
    }
    this.depth.set(d2); this.flux.set(f2); this.bed.set(b2);
    return filled;
  }

  /** Raw fields for renderers (row-major, resolution² each). */
  get surfaceData(): { bed: Float32Array; depth: Float32Array } {
    return { bed: this.bed, depth: this.depth };
  }

  private cellIndex(x: number, z: number): number {
    const n = this.resolution;
    const col = Math.min(n - 1, Math.max(0, Math.round((x / this.cfg.sizeX + 0.5) * (n - 1))));
    const row = Math.min(n - 1, Math.max(0, Math.round((z / this.cfg.sizeZ + 0.5) * (n - 1))));
    return row * n + col;
  }

  private substep(dt: number): void {
    const n = this.resolution;
    const { gravity, damping, openBoundaries } = this.cfg;
    const bed = this.bed;
    const depth = this.depth;
    const flux = this.flux;
    // Pipe cross-section/length lumped constant (Mei et al: A·g/l).
    const pipeK = (gravity * this.cellArea) / Math.max(this.cellX, this.cellZ);
    const keep = Math.max(0, 1 - damping * dt);

    // 1) accelerate pipes by head difference (+x, −x, +z, −z).
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const i = row * n + col;
        const head = bed[i] + depth[i];
        const f = i * 4;
        const accel = (nIdx: number, slot: number): void => {
          const dh = head - (bed[nIdx] + depth[nIdx]);
          flux[f + slot] = Math.max(0, flux[f + slot] * keep + pipeK * dh * dt);
        };
        if (col + 1 < n) accel(i + 1, 0);
        else flux[f] = openBoundaries ? Math.max(0, flux[f] * keep + pipeK * depth[i] * dt) : 0;
        if (col - 1 >= 0) accel(i - 1, 1);
        else flux[f + 1] = openBoundaries ? Math.max(0, flux[f + 1] * keep + pipeK * depth[i] * dt) : 0;
        if (row + 1 < n) accel(i + n, 2);
        else flux[f + 2] = openBoundaries ? Math.max(0, flux[f + 2] * keep + pipeK * depth[i] * dt) : 0;
        if (row - 1 >= 0) accel(i - n, 3);
        else flux[f + 3] = openBoundaries ? Math.max(0, flux[f + 3] * keep + pipeK * depth[i] * dt) : 0;

        // 2) a cell can never pipe out more water than it holds.
        const total = flux[f] + flux[f + 1] + flux[f + 2] + flux[f + 3];
        if (total > 0) {
          const maxOut = (depth[i] * this.cellArea) / dt;
          const k = Math.min(1, maxOut / total);
          flux[f] *= k;
          flux[f + 1] *= k;
          flux[f + 2] *= k;
          flux[f + 3] *= k;
        }
      }
    }

    // 3) integrate divergence into depths.
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const i = row * n + col;
        const f = i * 4;
        let net = -(flux[f] + flux[f + 1] + flux[f + 2] + flux[f + 3]);
        if (col + 1 < n) net += flux[(i + 1) * 4 + 1]; // neighbour's −x inflow
        if (col - 1 >= 0) net += flux[(i - 1) * 4]; // neighbour's +x inflow
        if (row + 1 < n) net += flux[(i + n) * 4 + 3];
        if (row - 1 >= 0) net += flux[(i - n) * 4 + 2];
        depth[i] = Math.max(0, depth[i] + (net * dt) / this.cellArea);
      }
    }
  }

  /**
   * The PATCH substep (shader gaps R6) — the GPU kernels (`ShallowWaterGpu`) are this, line for line:
   *   K1 per cell: each face's flux = max(0, f·keep + pipeK·Δhead·dt), × the viscosity factor, 0 across a solid;
   *      an edge face against `edgeBed` (open) or closed; then the outflow limiter k = min(1, d·A / (Σf·dt)).
   *   K2 per cell: depth += (Σin − Σout)·dt/A (clamped ≥ 0 — the gain is ledgered), then rain, infiltration,
   *      evaporation, drain — each ledgered.
   * K1 reads only depths and its OWN old fluxes; K2 reads the NEW fluxes — both are parallel-safe as written.
   */
  private patchSubstep(dt: number): void {
    const n = this.resolution;
    const { gravity, damping, openBoundaries } = this.cfg;
    const bed = this.bed, depth = this.depth, flux = this.flux;
    const A = this.cellArea;
    const pipeK = (gravity * A) / Math.max(this.cellX, this.cellZ);
    const keep = Math.max(0, 1 - damping * dt);
    const visc3 = 3 * dt * this.viscosity;
    const solid = this.solid, drain = this.drain, edge = this.edgeBed;
    const L = this.ledger;
    const slope = (this.slopeScratch ??= new Float32Array(n * n));
    const WET = 1e-4, inv2c = 0.5 / Math.max(this.cellX, this.cellZ);
    // K1 — the pipes (and the pre-step water-surface slope for K2's sheet fade)
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const i = row * n + col;
        const f = i * 4;
        const d = depth[i];
        if (solid && solid[i]) { flux[f] = 0; flux[f + 1] = 0; flux[f + 2] = 0; flux[f + 3] = 0; slope[i] = 0; continue; }
        const head = bed[i] + d;
        // the level over the WET neighbours (a dry / solid neighbour or the window's edge reads this cell's own level);
        // inlined — a closure per cell per substep was most of the oracle's cost
        const eR = col < n - 1 && depth[i + 1] > WET && !(solid && solid[i + 1]) ? bed[i + 1] + depth[i + 1] : head;
        const eL = col > 0 && depth[i - 1] > WET && !(solid && solid[i - 1]) ? bed[i - 1] + depth[i - 1] : head;
        const eU = row < n - 1 && depth[i + n] > WET && !(solid && solid[i + n]) ? bed[i + n] + depth[i + n] : head;
        const eD = row > 0 && depth[i - n] > WET && !(solid && solid[i - n]) ? bed[i - n] + depth[i - n] : head;
        const gx = (eR - eL) * inv2c;
        const gz = (eU - eD) * inv2c;
        slope[i] = Math.max(Math.abs(gx), Math.abs(gz));
        const vis = visc3 > 0 ? (d * d) / (d * d + visc3) : 1;
        // slot 0 +x (col+1), 1 −x (col−1), 2 +z (row+1), 3 −z (row−1); edge index: +x row, −x n+row, +z 2n+col, −z 3n+col
        for (let s = 0; s < 4; s++) {
          const nc = s === 0 ? col + 1 : s === 1 ? col - 1 : col;
          const nr = s === 2 ? row + 1 : s === 3 ? row - 1 : row;
          let out: number;
          if (nc >= 0 && nc < n && nr >= 0 && nr < n) {
            const j = nr * n + nc;
            if (solid && solid[j]) out = 0;
            else out = Math.max(0, flux[f + s] * keep + pipeK * (head - (bed[j] + depth[j])) * dt) * vis;
          } else if (openBoundaries) {
            const ghost = edge ? edge[s === 0 ? row : s === 1 ? n + row : s === 2 ? 2 * n + col : 3 * n + col] : bed[i];
            out = Math.max(0, flux[f + s] * keep + pipeK * (head - ghost) * dt) * vis;
          } else out = 0;
          flux[f + s] = out;
        }
        const total = flux[f] + flux[f + 1] + flux[f + 2] + flux[f + 3];
        if (total > 0) {
          const k = Math.min(1, (d * A) / (total * dt));
          flux[f] *= k; flux[f + 1] *= k; flux[f + 2] *= k; flux[f + 3] *= k;
        }
      }
    }
    // K2 — the column and its sources / sinks
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const i = row * n + col;
        const f = i * 4;
        // a cell a building now stands on holds no water (a solid marked over a pool: the water is displaced — drained)
        if (solid && solid[i]) { if (depth[i] > 0) { L.drained += depth[i] * A; depth[i] = 0; } continue; }
        let net = -(flux[f] + flux[f + 1] + flux[f + 2] + flux[f + 3]);
        if (col + 1 < n) net += flux[(i + 1) * 4 + 1]; else L.boundary += flux[f] * dt;
        if (col - 1 >= 0) net += flux[(i - 1) * 4]; else L.boundary += flux[f + 1] * dt;
        if (row + 1 < n) net += flux[(i + n) * 4 + 3]; else L.boundary += flux[f + 2] * dt;
        if (row - 1 >= 0) net += flux[(i - n) * 4 + 2]; else L.boundary += flux[f + 3] * dt;
        let dn = depth[i] + (net * dt) / A;
        if (dn < 0) { L.clampGain += -dn * A; dn = 0; }
        const r = this.rainRate ? this.rainRate[i] * dt : 0;
        dn += r; L.rain += r * A;
        const inf = this.infilRate ? Math.min(dn, this.infilRate[i] * dt) : 0;
        dn -= inf; L.infiltration += inf * A;
        const ev = Math.min(dn, this.evapRate * dt);
        dn -= ev; L.evaporation += ev * A;
        if (this.retain && this.sheetDrain > 0) {
          const fadeK = 1 - sstep(this.sheetSlope[0], this.sheetSlope[1], slope[i]);
          const ro = Math.min(Math.max(0, dn - this.retain[i]), this.sheetDrain * dt * fadeK);
          dn -= ro; L.runoff += ro * A;
        }
        if (drain && drain[i]) { L.drained += dn * A; dn = 0; }
        depth[i] = dn;
      }
    }
    this.substeps++;
  }
}
